import type {
    EntitlementDecision,
    Entitlements,
    EntitlementStatus,
    FeatureKey,
} from '@/api/entitlements';

export type EntitlementUsage = {
    used?: number | null;
    limit?: number | null;
    remaining?: number | null;
    renews_at?: string | null;
};

export type EntitlementStatusTone = 'success' | 'warning' | 'destructive' | 'info' | 'neutral';

export type BillingQuotaFeature = {
    feature: FeatureKey;
    label: string;
    singularUnitLabel: string;
    unitLabel: string;
    description: string;
};

const DAY_IN_MS = 24 * 60 * 60 * 1000;

export const BILLING_QUOTA_FEATURES: BillingQuotaFeature[] = [
    {
        feature: 'reports.create',
        label: 'Laudos',
        singularUnitLabel: 'laudo',
        unitLabel: 'laudos',
        description: 'Laudos criados no ciclo atual.',
    },
    {
        feature: 'ai.credits',
        label: 'Créditos de IA',
        singularUnitLabel: 'crédito',
        unitLabel: 'créditos',
        description: 'Gerações e melhorias de texto com IA.',
    },
    {
        feature: 'surveys.send',
        label: 'Formulários',
        singularUnitLabel: 'envio',
        unitLabel: 'envios',
        description: 'Formulários enviados para pacientes.',
    },
    {
        feature: 'tests.send',
        label: 'Instrumentos',
        singularUnitLabel: 'envio',
        unitLabel: 'envios',
        description: 'Instrumentos atribuídos aos pacientes.',
    },
    {
        feature: 'tests.correctWithAi',
        label: 'Correções com IA',
        singularUnitLabel: 'correção',
        unitLabel: 'correções',
        description: 'Consolidações automáticas de instrumentos.',
    },
    {
        feature: 'patientLinks.send',
        label: 'Links de pré-anamnese',
        singularUnitLabel: 'link',
        unitLabel: 'links',
        description: 'Links avulsos para cadastro e pré-anamnese.',
    },
    {
        feature: 'anamnesis.process',
        label: 'Anamneses',
        singularUnitLabel: 'processamento',
        unitLabel: 'processamentos',
        description: 'Arquivos de anamnese processados pela plataforma.',
    },
];

const PLAN_LABELS = {
    TRIAL: 'Teste gratuito',
    BASIC: 'Basic',
    INTERMEDIATE: 'Intermediate',
    PRO: 'Pro',
} satisfies Record<Entitlements['plan'], string>;

const STATUS_LABELS: Record<EntitlementStatus, string> = {
    trial: 'Teste gratuito ativo',
    trial_not_started: 'Teste gratuito disponível',
    trial_expired: 'Teste gratuito expirado',
    active: 'Assinatura ativa',
    trialing: 'Assinatura em teste',
    canceled: 'Assinatura cancelada',
    past_due: 'Pagamento pendente',
    incomplete: 'Assinatura incompleta',
    incomplete_expired: 'Assinatura expirada',
    unpaid: 'Pagamento em aberto',
};

export const formatEntitlementNumber = (value?: number | null) =>
    new Intl.NumberFormat('pt-BR').format(value ?? 0);

export const formatEntitlementDate = (value?: string | null) => {
    if (!value) return null;

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;

    return new Intl.DateTimeFormat('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
    }).format(date);
};

export const getTrialRemainingDays = (periodEnd?: string | null) => {
    if (!periodEnd) return null;

    const endDate = new Date(periodEnd);
    if (Number.isNaN(endDate.getTime())) return null;

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    endDate.setHours(0, 0, 0, 0);

    return Math.max(0, Math.ceil((endDate.getTime() - today.getTime()) / DAY_IN_MS));
};

export const getEntitlementPlanLabel = (plan?: Entitlements['plan']) => {
    if (!plan) return 'Plano indisponível';
    return PLAN_LABELS[plan] ?? plan;
};

export const getEntitlementStatusTone = (
    entitlements?: Entitlements | null,
): EntitlementStatusTone => {
    if (!entitlements) return 'neutral';
    if (!entitlements.active) return 'destructive';

    if (entitlements.status === 'trial') return 'info';
    if (entitlements.status === 'active' || entitlements.status === 'trialing') {
        return 'success';
    }

    return 'warning';
};

export const getEntitlementBlockedMessage = (
    decision?: EntitlementDecision | null,
    fallback = 'Sua assinatura atual não permite esta ação.',
) => {
    if (!decision || decision.allowed) return fallback;

    if (decision.reason === 'quota_exceeded') {
        if (decision.feature === 'reports.export') {
            if (decision.available_from_plan) {
                return `Você já usou sua exportação gratuita. Atualize para o plano ${decision.available_from_plan.name} para exportar outros laudos.`;
            }

            return 'Você já usou sua exportação gratuita. Escolha um plano para exportar outros laudos.';
        }

        if (decision.feature === 'reports.create' && decision.available_from_plan) {
            return `Você atingiu o limite de laudos do seu plano. Atualize para o plano ${decision.available_from_plan.name} para criar mais laudos.`;
        }

        if (decision.available_from_plan) {
            return `Você atingiu o limite de uso do seu plano. Atualize para o plano ${decision.available_from_plan.name} para continuar.`;
        }

        return 'Limite de uso do plano atingido. Verifique seus limites ou melhore seu plano para continuar.';
    }

    if (decision.reason === 'trial_not_started') {
        return 'Escolha começar o teste grátis ou conclua uma assinatura para liberar o acesso.';
    }

    if (decision.reason === 'trial_forfeited') {
        return 'Sua conta já teve uma assinatura paga. Escolha um plano para continuar.';
    }

    if (decision.available_from_plan) {
        return `Disponível no plano ${decision.available_from_plan.name}.`;
    }

    if (decision.reason === 'resource_cap_exceeded') {
        return 'Limite do plano atingido. Melhore seu plano para continuar.';
    }

    if (decision.reason === 'trial_expired') {
        return 'Seu teste gratuito expirou. Escolha um plano para continuar.';
    }

    if (decision.reason === 'subscription_inactive') {
        return 'Sua assinatura atual não está ativa.';
    }

    return fallback;
};

export const getEntitlementStatusLabel = (status?: EntitlementStatus) => {
    if (!status) return 'Status indisponível';
    return STATUS_LABELS[status] ?? status;
};

export const getEntitlementStatusDescription = (entitlements?: Entitlements | null) => {
    if (!entitlements) {
        return 'Não foi possível carregar os detalhes do seu plano agora.';
    }

    const periodEnd = formatEntitlementDate(entitlements.period_end);

    if (entitlements.status === 'trial') {
        return periodEnd
            ? `Seu teste gratuito está ativo até ${periodEnd}.`
            : 'Seu teste gratuito está ativo.';
    }

    if (entitlements.status === 'trial_not_started') {
        return 'Seu teste gratuito ainda não foi iniciado. Comece o teste grátis ou escolha um plano para liberar a plataforma.';
    }

    if (entitlements.status === 'trial_expired') {
        return periodEnd
            ? `Seu teste gratuito terminou em ${periodEnd}. Escolha um plano para continuar sem interrupções.`
            : 'Seu teste gratuito terminou. Escolha um plano para continuar sem interrupções.';
    }

    if (!entitlements.active) {
        return 'Sua assinatura não está ativa no momento. Regularize ou escolha um plano para liberar os recursos.';
    }

    if (entitlements.status === 'trialing') {
        return periodEnd
            ? `Sua assinatura está em período de teste até ${periodEnd}.`
            : 'Sua assinatura está em período de teste.';
    }

    return periodEnd
        ? `Seu ciclo atual renova em ${periodEnd}.`
        : 'Seu plano está ativo neste ciclo.';
};

export const isQuotaEntitlement = (
    decision?: EntitlementDecision,
): decision is EntitlementDecision & {
    rule: Extract<EntitlementDecision['rule'], { kind: 'quota' }>;
} => decision?.rule.kind === 'quota';

export const getEntitlementUsage = (decision?: EntitlementDecision): EntitlementUsage | null => {
    if (!isQuotaEntitlement(decision)) return null;

    return {
        used: decision.used ?? 0,
        limit: decision.limit ?? null,
        remaining: decision.remaining ?? null,
        renews_at: decision.renews_at ?? null,
    };
};
